import { AxiosResponse } from 'axios';

import { client, clientWithHttpResponse } from '@api/client';
import { URL } from '@api/urls';

import { IVendor, TCMNodeVendorTechnology } from '@interfaces/cmModule/dto/cmModuleDTO';
import { TNameTitle } from '@interfaces/common/common';
import { IDefaultUploadUrlApi, IDownloadUrl } from '@interfaces/common/file';
import { IFieldsMappingDTO, ISaveImportRulesDTO } from '@interfaces/import/dto/importDTO';
import { ISystemFields, TImportSiteType } from '@interfaces/import/import';

const {
	createSitesUploadUrl,
	importSitesTypes,
	sitesSampleUrl,
	importSitesGetAllFields,
	importSitesSaveImportRules,
	importSitesFieldSamples,
	importSitesSystemFields,
	importSitesFieldsMapping,
	importStatistics,
	importSitesExecute,
} = URL;

const getVendorImportSitesUrl = (vendor: string) => `sites/${vendor.toLowerCase()}/import/`;

export const getUploadURL = ({
	fileName,
	technology,
	vendor,
}: IDefaultUploadUrlApi & TCMNodeVendorTechnology<string, string | undefined>) => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${createSitesUploadUrl}`, {
		fileName,
		technology,
	});
};

export const getImportTypes = (vendor: string): Promise<TNameTitle[]> => {
	return client.get(`${getVendorImportSitesUrl(vendor)}${importSitesTypes}`);
};

export const runAllFields = ({ id, vendor }: TImportSiteType): Promise<void> => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${id}${importSitesGetAllFields}`, {});
};

export const getAllFields = ({ id, vendor }: TImportSiteType): Promise<AxiosResponse> => {
	return clientWithHttpResponse.get(`${getVendorImportSitesUrl(vendor)}${id}${importSitesGetAllFields}`);
};

export const getSampleUrl = (
	vendor: string,
): Promise<IDownloadUrl> => client.get(`${getVendorImportSitesUrl(vendor)}${sitesSampleUrl}`);

export const submitImportRules = ({
	id,
	skipEmptyFields,
	skipFields,
	importType,
	vendor,
}: ISaveImportRulesDTO & IVendor): Promise<void> => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${id}${importSitesSaveImportRules}`, {
		skipEmptyFields,
		skipFields,
		importType,
	});
};

export const runFieldSamples = ({ id, vendor }: TImportSiteType): Promise<void> => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${id}${importSitesFieldSamples}`, {});
};

export const getFieldSamples = ({ id, vendor }: TImportSiteType): Promise<AxiosResponse> => {
	return clientWithHttpResponse.get(`${getVendorImportSitesUrl(vendor)}${id}${importSitesFieldSamples}`);
};

export const getSystemFields = ({ id, vendor }: TImportSiteType): Promise<ISystemFields[]> => {
	return client.get(`${getVendorImportSitesUrl(vendor)}${importSitesSystemFields}?importId=${id}`);
};

export const submitFieldsMapping = ({
	id,
	fieldsMapping,
	vendor,
}: TImportSiteType & { fieldsMapping: Array<IFieldsMappingDTO> }): Promise<void> => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${id}${importSitesFieldsMapping}`, { fieldsMapping });
};

export const runImportStatistic = ({ id, vendor }: TImportSiteType): Promise<void> => {
	return client.post(`${getVendorImportSitesUrl(vendor)}${id}${importStatistics}`, {});
};

export const getImportStatistic = ({ id, vendor }: TImportSiteType): Promise<AxiosResponse> => {
	return clientWithHttpResponse.get(`${getVendorImportSitesUrl(vendor)}${id}${importStatistics}`);
};

export const approveImport = ({ id, vendor }: TImportSiteType): Promise<void> => {
	return client.get(`${getVendorImportSitesUrl(vendor)}${id}${importSitesExecute}`);
};
